"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";

/**
 * 매장 미등록 사용자에게 홈 상단 상시 노출되는 등록 유도 띠배너 (Phase 1.4b, 2026-05-12).
 *
 * 조건:
 *   1. hydrated
 *   2. 로그인
 *   3. 매장 0개 (/api/stores)
 *   4. localStorage dismiss 안 됨 (24시간 한 번씩 다시 노출)
 *
 * 매장 등록 시 자동으로 사라짐 (다음 페이지 로드 때 stores ≥ 1 감지).
 */

const DISMISS_KEY = "store_setup_banner_dismissed_at";
const DISMISS_HOURS = 24;

export function StoreSetupBanner() {
  const { token, isLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const [hasNoStore, setHasNoStore] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!hasHydrated || !isLoggedIn || !token) return;

    // dismiss 만료 체크
    const v = typeof window !== "undefined" ? localStorage.getItem(DISMISS_KEY) : null;
    if (v) {
      const ts = parseInt(v, 10);
      if (!isNaN(ts) && Date.now() - ts < DISMISS_HOURS * 3600 * 1000) {
        setDismissed(true);
        return;
      }
    }

    // 매장 조회 — 0개면 노출, 1+ 면 숨김
    axios
      .get<{ items: { id: number }[] }>("/api/stores", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setHasNoStore((r.data.items ?? []).length === 0))
      .catch(() => setHasNoStore(null));
  }, [hasHydrated, isLoggedIn, token]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof window !== "undefined") {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setDismissed(true);
  };

  if (!hasHydrated || !isLoggedIn || dismissed || hasNoStore !== true) return null;

  return (
    <Link
      href="/store/setup"
      className="block mx-4 mt-3 rounded-lg border border-primary/40 bg-primary/10 hover:bg-primary/15 transition-colors"
    >
      <div className="px-3 py-2.5 flex items-center gap-3">
        <span className="text-lg shrink-0">🏪</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-text leading-tight">
            매장 등록하고 잠금 해제
          </p>
          <p className="text-2xs text-text-sub leading-tight mt-0.5">
            매출 분석 · 메뉴 ROI · 재고 알림 자동 — 3분이면 끝
          </p>
        </div>
        <span className="text-primary text-xs font-bold shrink-0">시작 →</span>
        <button
          onClick={handleDismiss}
          aria-label="닫기"
          className="text-text-sub hover:text-text text-xs shrink-0 -mr-1 px-1"
        >
          ✕
        </button>
      </div>
    </Link>
  );
}
